import { ListenerModifier } from "./constants.js";
/**
 * Modifiers that affect event listener options accepted by the `addEventListener` method.
 */
const optionModifiers = [
    ListenerModifier.Capture,
    ListenerModifier.Once,
    ListenerModifier.Passive,
];
/**
 * Modifiers that affect how events are handled by the listener.
 *
 * Use of these modifiers is will result in the provided handler being wrapped
 * in a new function that applies the modifier behavior.
 *
 * As a result, it is not recommended to use these modifiers inside render functions
 * where the handler would be recreated on each render.
 */
const eventHandlingModifiers = [
    ListenerModifier.Stop,
    ListenerModifier.Prevent,
    ListenerModifier.Self,
];
class ModifiedListenerBuilder {
    #type;
    #modifiers = new Set();
    constructor(type) {
        this.#type = type;
    }
    #getOptionsFromModifiers() {
        const options = {};
        let optionWasSet = false;
        for (const modifier of optionModifiers) {
            if (this.#modifiers.has(modifier)) {
                options[modifier] = true;
                optionWasSet = true;
            }
        }
        return optionWasSet ? options : undefined;
    }
    #getDeclarationHandler(handler) {
        const hasEventHandlingModifiers = eventHandlingModifiers.some((modifier) => this.#modifiers.has(modifier));
        if (!hasEventHandlingModifiers) {
            return handler;
        }
        const modifiers = this.#modifiers;
        return function (event) {
            if (modifiers.has(ListenerModifier.Stop)) {
                event.stopPropagation();
            }
            if (modifiers.has(ListenerModifier.Prevent)) {
                event.preventDefault();
            }
            if (modifiers.has(ListenerModifier.Self)) {
                if (event.target !== event.currentTarget) {
                    return;
                }
            }
            handler.call(this, event);
        };
    }
    #getDeclarationOptions(options) {
        const modifiersOptions = this.#getOptionsFromModifiers();
        if (!modifiersOptions && !options) {
            return undefined;
        }
        return {
            ...modifiersOptions,
            ...options,
        };
    }
    addModifier(modifier) {
        this.#modifiers.add(modifier);
    }
    defineListener = (handler, options) => {
        return {
            type: this.#type,
            handler: this.#getDeclarationHandler(handler),
            options: this.#getDeclarationOptions(options),
        };
    };
}
function createListenerBuilder() {
    return new Proxy(defineListener, {
        get: (_target, propertyName, _receiver) => {
            if (typeof propertyName !== "string") {
                throw new Error("Invalid event type. It must be a string.");
            }
            const modifiedListener = new ModifiedListenerBuilder(propertyName);
            function createModifierProxy() {
                return new Proxy(modifiedListener.defineListener, {
                    get: (_target, propertyName, _receiver) => {
                        if (!isListenerModifier(propertyName)) {
                            throw new Error("Invalid property name. The property must be a listener modifier.");
                        }
                        modifiedListener.addModifier(propertyName);
                        return createModifierProxy();
                    },
                });
            }
            return createModifierProxy();
        },
    });
}
export const listen = createListenerBuilder();
function defineListener(type, handler, options) {
    return {
        type,
        handler,
        options,
    };
}
function isListenerModifier(value) {
    return Object.values(ListenerModifier).includes(value);
}
